/**
 * Governance Center Page — M6.2 Phase 3
 * Internal governance documents listing with status and version tracking
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  FileText, Shield, BookOpen, Search, Filter, Eye, Edit2,
  Loader2, AlertTriangle, CheckCircle2, Clock, Archive,
  User, Calendar, ChevronRight, Bot, Database, Lock, PenTool
} from 'lucide-react';

// Category icons mapping
const CATEGORY_ICONS = {
  ai_governance: { icon: Bot, color: 'text-purple-400', bg: 'bg-purple-500/15' },
  data_integrity: { icon: Database, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
  privacy: { icon: Lock, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  scriber_disclosure: { icon: PenTool, color: 'text-amber-400', bg: 'bg-amber-500/15' },
  standards_applicability: { icon: BookOpen, color: 'text-blue-400', bg: 'bg-blue-500/15' }
};

const CATEGORY_NAMES = {
  ai_governance: 'AI Use & Human Review Policy',
  data_integrity: 'Data Integrity Policy',
  privacy: 'Privacy & Sensitive Data Handling Policy',
  scriber_disclosure: 'SCRIBER Disclosure Policy',
  standards_applicability: 'Standards Applicability Policy'
};

// Status badge component
const StatusBadge = ({ status }) => {
  const configs = {
    draft: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: Clock, label: 'Draft' },
    under_review: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Eye, label: 'Under Review' },
    active: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle2, label: 'Active' },
    archived: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: Archive, label: 'Archived' }
  };
  
  const config = configs[status] || configs.draft;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Document card component
const DocumentCard = ({ document, onClick }) => {
  const categoryConfig = CATEGORY_ICONS[document.category] || { icon: FileText, color: 'text-gray-400', bg: 'bg-gray-500/15' };
  const CategoryIcon = categoryConfig.icon;
  
  return (
    <div 
      className="qms-card hover:border-[#36A9E1]/30 transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`governance-doc-card-${document.document_id}`}
    >
      <div className="qms-card-body p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className={`p-2 rounded-lg ${categoryConfig.bg}`}>
            <CategoryIcon className={categoryConfig.color} size={24} />
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={document.status} />
            <span className="text-[#8B95A5] text-xs">{document.version}</span>
          </div>
        </div>
        
        <h3 className="text-white font-semibold text-sm mb-2 group-hover:text-[#36A9E1] transition-colors line-clamp-2">
          {document.title}
        </h3>
        
        <p className="text-[#8B95A5] text-xs mb-4 line-clamp-2">
          {CATEGORY_NAMES[document.category] || document.category}
        </p>
        
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-[#8B95A5]">
            <User size={12} />
            {document.owner_role}
          </div>
          <div className="flex items-center gap-1 text-[#36A9E1] group-hover:text-[#38BDF8] transition-colors">
            View <ChevronRight size={14} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Governance Center Page
const GovernanceCenterPage = () => {
  const { t } = useTranslation(['common']);
  const { isAdmin, isAuditor } = useAuth();
  const navigate = useNavigate();
  
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [docsRes, statsRes] = await Promise.all([
        fetch(`${API}/governance/documents`, { credentials: 'include' }),
        fetch(`${API}/governance/documents/stats`, { credentials: 'include' })
      ]);
      
      if (docsRes.status === 403) {
        setError('Access denied. Governance documents are restricted to governance roles.');
        setLoading(false);
        return;
      }
      
      if (!docsRes.ok || !statsRes.ok) {
        throw new Error('Failed to load governance documents');
      }
      
      const [docsData, statsData] = await Promise.all([
        docsRes.json(),
        statsRes.json()
      ]);
      
      setDocuments(docsData);
      setStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Error loading governance documents:', err);
      setError('Failed to load governance documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filtered documents
  const filteredDocuments = documents.filter(doc => {
    if (categoryFilter !== 'all' && doc.category !== categoryFilter) return false;
    if (statusFilter !== 'all' && doc.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        doc.title.toLowerCase().includes(q) ||
        doc.category.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Access check
  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:center.title')}>
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <Shield className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="text-white font-semibold mb-2">Access Restricted</h3>
            <p className="text-[#8B95A5] text-sm">
              Governance documents are restricted to governance roles (Admin, Auditor).
            </p>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (loading) {
    return (
      <QMSLayout title={t('governance:center.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('governance:center.title')}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="text-[#36A9E1]" size={28} />
          <h1 className="font-['Syne'] text-2xl font-bold text-white">Governance Center</h1>
        </div>
        <p className="text-[#8B95A5] text-sm">
          Platform governance policies, procedures, and compliance documentation
        </p>
      </div>

      {/* Draft Notice Banner */}
      <div className="qms-card border-amber-500/30 mb-6">
        <div className="qms-card-body p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-amber-400 font-medium text-sm mb-1">Draft Documents — Review Required</h4>
              <p className="text-[#8B95A5] text-xs leading-relaxed">
                Documents marked as "Draft" are working versions for internal review. They do not represent final approved corporate policy until formally signed off by designated approvers.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">Total Documents</div>
            <div className="text-white text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">Draft</div>
            <div className="text-amber-400 text-2xl font-bold">{stats.draft}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">Under Review</div>
            <div className="text-blue-400 text-2xl font-bold">{stats.under_review}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">Active</div>
            <div className="text-emerald-400 text-2xl font-bold">{stats.active}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">Archived</div>
            <div className="text-gray-400 text-2xl font-bold">{stats.archived}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="qms-card mb-6">
        <div className="qms-card-body p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={16} />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm placeholder-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
                data-testid="governance-search-input"
              />
            </div>
            
            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
            >
              <option value="all">All Categories</option>
              <option value="ai_governance">🤖 AI Governance</option>
              <option value="data_integrity">🔐 Data Integrity</option>
              <option value="privacy">🔒 Privacy</option>
              <option value="scriber_disclosure">✍️ SCRIBER Disclosure</option>
              <option value="standards_applicability">📚 Standards Applicability</option>
            </select>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="draft">📝 Draft</option>
              <option value="under_review">👁️ Under Review</option>
              <option value="active">✅ Active</option>
              <option value="archived">📦 Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="qms-card border-red-500/30 mb-6">
          <div className="qms-card-body p-4 text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {error}
          </div>
        </div>
      )}

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments.map(doc => (
          <DocumentCard
            key={doc.document_id}
            document={doc}
            onClick={() => navigate(`/governance/${doc.document_id}`)}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredDocuments.length === 0 && !loading && !error && (
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <FileText className="mx-auto mb-4 text-[#8B95A5]" size={48} />
            <h3 className="text-white font-semibold mb-2">No Documents Found</h3>
            <p className="text-[#8B95A5] text-sm">
              {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No governance documents have been created yet'}
            </p>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default GovernanceCenterPage;
